import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import SlidingDrawer from '../../components/SlidingDrawer';

const QuestionBank = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [filter, setFilter] = useState('');
    const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false);
    const [selectedDept, setSelectedDept] = useState('');

    useEffect(() => {
        fetchQuestions();
        fetchDepartments();
    }, []);

    const fetchQuestions = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/questions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQuestions(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/master/departments`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDepartments(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAssign = (id) => {
        const q = questions.find(q => q._id === id);
        setSelectedQuestion(q);
        setSelectedDept(q.department || '');
        setIsAssignDrawerOpen(true);
    };

    const confirmAssignment = async () => {
        if (!selectedDept) {
            Swal.fire('Required', 'Please select a department for routing.', 'warning');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/api/questions/${selectedQuestion._id}/assign`, {
                department: selectedDept
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire({
                title: 'Operation Successful',
                text: `Legislative question routed to the ${selectedDept} Ministry.`,
                icon: 'success',
                confirmButtonColor: '#C0722C'
            });
            setIsAssignDrawerOpen(false);
            fetchQuestions();
        } catch (err) {
            if (err.response && err.response.status === 404) {
                Swal.fire('Assignment Failed', 'No Minister currently registered for this department.', 'warning');
            } else {
                Swal.fire('Routing Failed', 'System error during departmental dispatch.', 'error');
            }
        }
    };

    const handleNotify = async (id, e) => {
        e.stopPropagation();
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/questions/${id}/notify`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire({
                title: 'Notification Dispatched',
                text: 'High-priority reminder sent to the Department Minister.',
                icon: 'success',
                confirmButtonColor: '#C0722C'
            });
            fetchQuestions();
        } catch (err) {
            Swal.fire('Error', 'Failed to send notification', 'error');
        }
    };

    const handlePublish = async (id, e) => {
        e.stopPropagation();
        Swal.fire({
            title: 'Confirm Publication',
            text: "This response will be made available on the public legislative portal.",
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#2E7D32',
            confirmButtonText: 'Publish Record'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem('token');
                    await axios.post(`${API_BASE_URL}/api/questions/${id}/publish`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    Swal.fire('Published', 'Question record is now public.', 'success');
                    fetchQuestions();
                } catch (err) {
                    Swal.fire('Error', 'Failed to publish question', 'error');
                }
            }
        });
    };

    const isOverdue = (assignedAt) => {
        if (!assignedAt) return false;
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        return (new Date() - new Date(assignedAt)) > sevenDays;
    };

    const openDetails = (q) => {
        setSelectedQuestion(q);
        setIsDrawerOpen(true);
    };

    const DataBlock = ({ label, value }) => (
        <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#F9F9F9', borderRadius: '12px', border: '1px solid #F0F0F0' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>
                {label}
            </label>
            <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '600', lineHeight: '1.5' }}>
                {value || 'N/A'}
            </div>
        </div>
    );

    const isAnsweredWithinSLA = (q) => {
        if (q.status !== 'ANSWERED' || !q.assignedAt || !q.answeredAt) return false;
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        return (new Date(q.answeredAt) - new Date(q.assignedAt)) <= sevenDays;
    };

    const filteredQuestions = questions.filter(q =>
        q.subject.toLowerCase().includes(filter.toLowerCase()) ||
        q.department.toLowerCase().includes(filter.toLowerCase()) ||
        q.memberName.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div style={{ fontFamily: '"Outfit", sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h2 style={{ color: '#2C1A10', margin: 0, fontWeight: '800', fontSize: '28px' }}>Question Bank</h2>
                    <p style={{ margin: '5px 0 0 0', color: '#888', fontSize: '14px' }}>Question Processing & Departmental Routing Protocol</p>
                </div>
                <div style={{ position: 'relative', width: '100%', maxWidth: '350px', minWidth: '250px' }}>
                    <input
                        type="text"
                        placeholder="Filter by Subject, Dept or Member..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 15px 12px 40px',
                            borderRadius: '12px',
                            border: '1px solid #E0E0E0',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#C0722C';
                            e.target.style.boxShadow = '0 0 0 4px rgba(192, 114, 44, 0.1)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#E0E0E0';
                            e.target.style.boxShadow = '0 2px 5px rgba(0,0,0,0.02)';
                        }}
                    />
                    <svg style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', pointerEvents: 'none' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                </div>
            </div>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                title="Legislative Question Dossier"
            >
                {selectedQuestion && (
                    <div style={{ animation: 'fadeIn 0.4s ease' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                            <span style={{
                                padding: '6px 14px',
                                borderRadius: '10px',
                                backgroundColor: selectedQuestion.status === 'PUBLISHED' ? '#00796B' : (selectedQuestion.status === 'ANSWERED' ? '#1A237E' : (selectedQuestion.status === 'ASSIGNED' ? '#2E7D32' : (selectedQuestion.status === 'NEW' ? '#C0722C' : (selectedQuestion.status === 'PENDING' ? '#888' : '#C0722C')))),
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: '800',
                                textTransform: 'uppercase'
                            }}>{selectedQuestion.status}</span>
                            <span style={{ fontSize: '12px', color: '#888', fontWeight: '600' }}>
                                Ref ID: #{selectedQuestion._id.slice(-6).toUpperCase()}
                            </span>
                        </div>

                        <DataBlock label="Subject Line" value={selectedQuestion.subject} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <DataBlock label="Department" value={selectedQuestion.department} />
                            <DataBlock label="Asking Member" value={selectedQuestion.memberName} />
                        </div>

                        <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#FDF8F3', borderRadius: '16px', border: '1px solid #F0E8DB' }}>
                            <label style={{ display: 'block', fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>
                                Question Narrative
                            </label>
                            <div style={{ fontSize: '15px', color: '#2C1A10', lineHeight: '1.7', whiteSpace: 'pre-line' }}>
                                {selectedQuestion.questionText}
                            </div>
                        </div>

                        {selectedQuestion.answerText && (
                            <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#F4FBF5', borderRadius: '16px', border: '1px solid #A5D6A7' }}>
                                <label style={{ display: 'block', fontSize: '10px', fontWeight: '800', color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>
                                    Ministerial Response
                                </label>
                                <div style={{ fontSize: '15px', color: '#1B5E20', lineHeight: '1.7', whiteSpace: 'pre-line', fontWeight: '500' }}>
                                    {selectedQuestion.answerText}
                                </div>
                                <div style={{ fontSize: '11px', color: '#2E7D32', marginTop: '10px', textAlign: 'right', fontWeight: '700' }}>
                                    SUBMITTED ON: {new Date(selectedQuestion.answeredAt).toLocaleString()}
                                </div>
                            </div>
                        )}

                        {selectedQuestion.assignedAt && (
                            <DataBlock label="Assigned On" value={new Date(selectedQuestion.assignedAt).toLocaleString()} />
                        )}

                        <div style={{ marginTop: '40px', display: 'flex', gap: '15px' }}>
                            {selectedQuestion.status === 'PENDING' || selectedQuestion.status === 'NEW' ? (
                                <button
                                    onClick={() => handleAssign(selectedQuestion._id)}
                                    style={{
                                        flex: 1, padding: '16px', backgroundColor: '#2C1A10', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer'
                                    }}
                                >
                                    Reroute to Department
                                </button>
                            ) : null}
                            <button
                                onClick={() => setIsDrawerOpen(false)}
                                style={{
                                    flex: 1, padding: '16px', backgroundColor: '#fff', color: '#2C1A10', border: '1px solid #E0E0E0', borderRadius: '12px', fontWeight: '800', cursor: 'pointer'
                                }}
                            >
                                Close Dossier
                            </button>
                        </div>
                    </div>
                )}
            </SlidingDrawer>

            <SlidingDrawer
                isOpen={isAssignDrawerOpen}
                onClose={() => setIsAssignDrawerOpen(false)}
                title="Departmental Routing Protocol"
            >
                {selectedQuestion && (
                    <div style={{ animation: 'fadeIn 0.4s ease' }}>
                        <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#F9F9F9', borderRadius: '16px', border: '1px solid #E0E0E0' }}>
                            <div style={{ fontSize: '10px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Active Question</div>
                            <div style={{ fontSize: '18px', fontWeight: '800', color: '#2C1A10' }}>{selectedQuestion.subject}</div>
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>Member: {selectedQuestion.memberName}</div>
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>
                                Target Jurisdiction (Department) <span style={{ color: '#C0722C' }}>*</span>
                            </label>
                            <select
                                value={selectedDept}
                                onChange={(e) => setSelectedDept(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    border: '1.5px solid #E0E0E0',
                                    fontSize: '15px',
                                    color: '#2C1A10',
                                    backgroundColor: '#fff',
                                    outline: 'none',
                                    fontWeight: '600'
                                }}
                            >
                                <option value="">Select Administrative Department</option>
                                {departments.map((dept, idx) => (
                                    <option key={idx} value={dept.name}>{dept.name}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ padding: '20px', backgroundColor: '#FFF9F5', borderRadius: '12px', border: '1px solid #FFE8D6', marginBottom: '30px' }}>
                            <div style={{ color: '#C0722C', fontSize: '13px', fontWeight: '600', lineHeight: '1.5' }}>
                                <strong>Notice:</strong> This action will dispatch the question to the Minister of the selected department. The 7-day SLA response window will initialize upon confirmation.
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '15px' }}>
                            <button
                                onClick={confirmAssignment}
                                style={{
                                    flex: 2, padding: '16px', backgroundColor: '#2C1A10', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontSize: '14px'
                                }}
                            >
                                Confirm Dispatch
                            </button>
                            <button
                                onClick={() => setIsAssignDrawerOpen(false)}
                                style={{
                                    flex: 1, padding: '16px', backgroundColor: '#fff', color: '#666', border: '1px solid #E0E0E0', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </SlidingDrawer>

            <div style={{ backgroundColor: '#fff', borderRadius: '24px', padding: '30px', boxShadow: '0 15px 40px rgba(0,0,0,0.03)', border: '1px solid #F0F0F0', minHeight: '500px' }}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                        <div style={{ fontSize: '14px', color: '#888', fontWeight: '700', letterSpacing: '1px' }}>SYNCHRONIZING REPOSITORY...</div>
                    </div>
                ) : (
                    filteredQuestions.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', textAlign: 'center' }}>
                            <div style={{
                                width: '100px', height: '100px', backgroundColor: '#FDF8F3', borderRadius: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', marginBottom: '25px'
                            }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                            </div>
                            <h3 style={{ color: '#2C1A10', margin: '0 0 10px 0', fontSize: '22px', fontWeight: '800' }}>Registry Clear</h3>
                            <p style={{ color: '#888', fontSize: '15px', margin: 0, maxWidth: '250px', lineHeight: '1.6' }}>No legislative records matched your current filter criteria.</p>
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 12px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ color: '#888', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                                    <th style={{ padding: '0 20px' }}>Subject & Identity</th>
                                    <th style={{ padding: '0 20px' }}>Jurisdiction</th>
                                    <th style={{ padding: '0 20px' }}>Timeline</th>
                                    <th style={{ padding: '0 20px' }}>Status</th>
                                    <th style={{ padding: '0 20px', textAlign: 'right' }}>Directives</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredQuestions.map(q => {
                                    const overdue = q.status === 'ASSIGNED' && isOverdue(q.assignedAt);
                                    const highEfficiency = isAnsweredWithinSLA(q);
                                    return (
                                        <tr key={q._id} style={{
                                            backgroundColor: q.status === 'PUBLISHED' ? '#F1F8F9' : (highEfficiency ? '#F1F8E9' : (overdue ? '#FFF1F1' : '#FDF8F3')),
                                            border: highEfficiency ? '1.5px solid #C5E1A5' : (overdue ? '1.5px solid #FFCDCD' : '1px solid transparent'),
                                            transition: 'all 0.2s ease',
                                            borderRadius: '16px',
                                            cursor: 'pointer'
                                        }} onClick={() => openDetails(q)}>
                                            <td style={{ padding: '20px', borderRadius: '16px 0 0 16px' }}>
                                                <div style={{ fontWeight: '800', color: overdue ? '#D32F2F' : '#2C1A10', fontSize: '15px' }}>{q.subject}</div>
                                                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Proponent: {q.memberName}</div>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#444' }}>{q.department}</div>
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <div style={{ fontSize: '13px', color: '#666' }}>
                                                    {q.assignedAt ? `Assigned: ${new Date(q.assignedAt).toLocaleDateString()}` : `Created: ${new Date(q.createdAt).toLocaleDateString()}`}
                                                </div>
                                                {overdue && (
                                                    <div style={{ fontSize: '11px', color: '#D32F2F', fontWeight: '800', marginTop: '4px' }}>SLA BREACH: 7+ DAYS</div>
                                                )}
                                            </td>
                                            <td style={{ padding: '20px' }}>
                                                <span style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '10px',
                                                    backgroundColor: q.status === 'PUBLISHED' ? '#00796B' : (q.status === 'ANSWERED' ? '#1A237E' : (overdue ? '#D32F2F' : (q.status === 'ASSIGNED' ? '#2E7D32' : (q.status === 'NEW' ? '#C0722C' : (q.status === 'PENDING' ? '#888' : '#C0722C'))))),
                                                    color: '#fff',
                                                    fontSize: '11px',
                                                    fontWeight: '800',
                                                    textTransform: 'uppercase'
                                                }}>{q.status}</span>
                                                {highEfficiency && (
                                                    <span style={{ marginLeft: '8px', color: '#2E7D32', fontSize: '16px' }} title="Answered within SLA">✓</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '20px', borderRadius: '0 16px 16px 0', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                    {q.status === 'PENDING' || q.status === 'NEW' ? (
                                                        <button
                                                            onClick={() => handleAssign(q._id)}
                                                            style={{
                                                                padding: '8px 16px', backgroundColor: '#2C1A10', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                                                            }}
                                                        >
                                                            Assign
                                                        </button>
                                                    ) : q.status === 'ANSWERED' ? (
                                                        <button
                                                            onClick={(e) => handlePublish(q._id, e)}
                                                            style={{
                                                                padding: '8px 16px', backgroundColor: '#2E7D32', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                                                            }}
                                                        >
                                                            Publish
                                                        </button>
                                                    ) : overdue && !q.notified ? (
                                                        <button
                                                            onClick={(e) => handleNotify(q._id, e)}
                                                            style={{
                                                                padding: '8px 16px', backgroundColor: '#FF5252', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', animation: 'pulse 2s infinite'
                                                            }}
                                                        >
                                                            Notify
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => openDetails(q)}
                                                            style={{
                                                                padding: '8px 16px', backgroundColor: '#fff', color: '#2C1A10', border: '1px solid #E0E0E0', borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: '700'
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )
                )}
            </div>
            <style>
                {`
                @keyframes pulse {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7); }
                    70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(255, 82, 82, 0); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 82, 82, 0); }
                }
                `}
            </style>
        </div>
    );
};

export default QuestionBank;
